import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, X, Sparkles, Loader2, Send, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { generateId } from '../../lib/generateId';
import type { Schwachstelle } from '../../lib/section4Schwachstelle';
import { suggestBulk, formatGenerationError } from '../../lib/generationClient';
import { API_BASE } from '../../lib/apiBase';
import { resolveBeratungsthema } from '../../lib/beratungsthema';
import { GeneratingTipsPopup } from '../GeneratingTipsPopup';
// Datenmodell + Migration ausgelagert nach lib/, damit diese Datei nur noch
// Komponenten exportiert (React-Fast-Refresh-Warnung, #10).
import { migrateMassnahme, type Massnahme, type Section5Data } from '../../lib/section5Massnahme';
import { RichListInput } from '../RichListInput';
import { htmlToPlainText, isEmptyContent } from '../../lib/richText';

function createEmpty(schwachstelleId = ''): Massnahme {
  return { id: generateId(), schwachstelleId, bezeichnung: '', beschreibung: '', aufwand: '' };
}

// ── Drift-Keywords (MVP) ───────────────────────────────────────
const DRIFT_KEYWORDS = [
  'buchungsstrategie', 'markenarchitektur', 'gaestebindung', 'gästebindung',
  'angebotssteuerung', 'kommunikationswege', 'vertriebskanal', 'vertriebsstrategie',
  'seo', 'social media marketing', 'social-media-marketing',
  'markenpositionierung', 'kundenbindung', 'preisgestaltung',
];

// ── Props ──────────────────────────────────────────────────────
interface Section5ActionPlanProps {
  darkMode: boolean;
  onDataChange: (data: Section5Data) => void;
  onGenerate: () => void;
  customerData?: {
    name: string;
    wirtschaftszweig?: string;
    geschaeftsgegenstand?: string;
    ort?: string;
  };
  formData?: Record<string, string>;
  initialData?: Section5Data;
  schwachstellenText?: string;  // Legacy: HTML-Text (nicht fuer Datenlogik verwenden)
  schwachstellenData?: Schwachstelle[];  // Strukturierte Daten = Source of Truth
  schwachstellenVersion?: number;
  isAdmin?: boolean;
}

export function Section5ActionPlan({
  darkMode, onDataChange, onGenerate, customerData, formData, initialData,
  schwachstellenText, schwachstellenData, schwachstellenVersion, isAdmin,
}: Section5ActionPlanProps) {

  const migrateInitial = (data?: Massnahme[]): Massnahme[] => {
    if (!data?.length) return [createEmpty()];
    return data.map(migrateMassnahme);
  };

  const [massnahmenEntries, setMassnahmenEntries] = useState<Massnahme[]>(
    migrateInitial(initialData?.massnahmen as any)
  );

  const [showPromptPopup, setShowPromptPopup] = useState(false);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [promptError, setPromptError] = useState<string | null>(null);
  const [bulkSuggesting, setBulkSuggesting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [suggestPromptSource, setSuggestPromptSource] = useState<'standard' | 'lokal' | 'bearbeitet'>('standard');
  const [lastSyncedVersion, setLastSyncedVersion] = useState(schwachstellenVersion || 0);
  const [tipsPopupOpen, setTipsPopupOpen] = useState(false);
  const [tipsPopupError, setTipsPopupError] = useState<string | null>(null);

  // ── Notify parent (stable ref to avoid infinite loops) ──────
  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current({ massnahmen: massnahmenEntries });
  }, [massnahmenEntries]);

  // ── Stale-Erkennung ──────────────────────────────────────────
  const isStale = (schwachstellenVersion || 0) > lastSyncedVersion && lastSyncedVersion > 0;

  // ── Verwaiste / nicht-zugeordnete Massnahmen ─────────────────
  const schwachstelleIds = useMemo(
    () => new Set((schwachstellenData || []).map(s => s.id)),
    [schwachstellenData]
  );

  const orphanedMassnahmen = useMemo(
    () => massnahmenEntries.filter(m => m.schwachstelleId && !schwachstelleIds.has(m.schwachstelleId)),
    [massnahmenEntries, schwachstelleIds]
  );

  const unassignedMassnahmen = useMemo(
    () => massnahmenEntries.filter(m => !m.schwachstelleId),
    [massnahmenEntries]
  );

  const schwachstellenOhneMassnahme = useMemo(() => {
    const assignedIds = new Set(massnahmenEntries.map(m => m.schwachstelleId).filter(Boolean));
    return (schwachstellenData || []).filter(s => !assignedIds.has(s.id));
  }, [schwachstellenData, massnahmenEntries]);

  // ── Drift-Warnung ────────────────────────────────────────────
  const driftWarning = useMemo(() => {
    const thema = resolveBeratungsthema(formData || {});
    if (!thema) return false;
    return massnahmenEntries.some(m => {
      const text = `${htmlToPlainText(m.bezeichnung)} ${htmlToPlainText(m.beschreibung)}`.toLowerCase();
      return DRIFT_KEYWORDS.some(kw => text.includes(kw));
    });
  }, [massnahmenEntries, formData]);

  // ── Validierung ──────────────────────────────────────────────
  const handleGenerate = () => {
    onGenerate();
  };

  // ── Entry-Operationen ────────────────────────────────────────
  const updateEntry = (id: string, field: keyof Massnahme, value: string) => {
    setMassnahmenEntries(prev =>
      prev.map(e => e.id === id ? { ...e, [field]: value } : e)
    );
    setValidationErrors(prev => {
      const next = new Set(prev);
      if (field === 'schwachstelleId') next.delete(`${id}-ref`);
      if (field === 'bezeichnung') next.delete(`${id}-bezeichnung`);
      return next;
    });
  };

  const removeEntry = (id: string) => {
    if (massnahmenEntries.length <= 1) return;
    setMassnahmenEntries(prev => prev.filter(e => e.id !== id));
  };

  const addEntry = () => {
    setMassnahmenEntries(prev => [...prev, createEmpty()]);
  };

  // ── Suggest-Prompt ───────────────────────────────────────────
  const STORAGE_KEY = 'suggest-prompt-action-plan';

  const buildDefaultPrompt = () => {
    const name = customerData?.name || '';
    const branche = customerData?.wirtschaftszweig || '';
    const thema = resolveBeratungsthema(formData || {});
    const existing = massnahmenEntries.map(e => htmlToPlainText(e.bezeichnung).trim()).filter(Boolean);

    // Strukturierte Schwachstellen als Kontext (Source of Truth, kein HTML)
    const schwachstellenKontext = (schwachstellenData || []).map((s, i) =>
      `${i + 1}. [ID: ${s.id}] Problem: ${s.problem}\n   Ursache: ${s.ursache}\n   Auswirkung: ${s.auswirkung}`
    ).join('\n\n');

    return `Erstelle pro Schwachstelle genau EINE strategische Handlungsempfehlung fuer Abschnitt 5.

WICHTIGSTE REGEL:
- Generiere IMMER Vorschlaege, auch bei unvollstaendigen Informationen
- Keine Rueckfragen, keine Erklaerungen, keine Kommentare
- Antworte ausschliesslich mit dem JSON-Objekt

UNTERNEHMEN:
- Name: ${name}
- Branche/WZ: ${branche}
- Beratungsthema: ${thema}

SCHWACHSTELLEN (verbindliche Grundlage):
${schwachstellenKontext || 'Keine Schwachstellen vorhanden – leite typische Schwachstellen der Branche ab.'}

${existing.length ? `Bereits vorhandene Massnahmen (NICHT wiederholen):\n${existing.map(s => `- ${s}`).join('\n')}\n` : ''}
DIREKTER BEZUG:
- Jede Massnahme muss konkret die jeweilige Schwachstelle adressieren.
- Allgemeine oder generische Massnahmen ohne klaren Bezug sind unzulaessig.
- Es duerfen keine neuen Themen eingefuehrt werden.
- Alle Massnahmen muessen im Rahmen des Beratungsthemas "${thema}" bleiben.

BEZUG ZUR SCHWACHSTELLE:
- Die Beschreibung der Massnahme muss den fachlichen Bezug zur jeweiligen Schwachstelle klar erkennen lassen.
- Der Loesungsansatz muss inhaltlich direkt aus dem beschriebenen Problem ableitbar sein.
- Die Bezeichnung und Beschreibung sollen zentrale Begriffe oder den fachlichen Kern der Schwachstelle erkennbar aufgreifen.
- Allgemeine Massnahmen ohne erkennbaren Problembezug sind unzulaessig.

STRATEGISCHE EBENE:
- Fokus auf Konzepte, Strukturen und Vorgehensweisen.
- Keine technischen Detailumsetzungen.
- Keine konkreten Tools, Produkte oder Anbieter nennen.
- VERBOTEN: operative Sprache wie "implementieren", "installieren", "einrichten", "konfigurieren", "Erfassung aller Systeme"

HILFE ZUR SELBSTHILFE:
- Konkret heisst: die Beschreibung muss sagen, WAS das Unternehmen selbst erheben, pruefen oder entscheiden soll (z. B. Kundengespraeche auswerten, eigene Anfragen analysieren, vorhandene Daten sichten, Wettbewerb vergleichen) - nicht WIE es technisch umgesetzt wird.
- Keine Tools, Software oder Anbieter nennen, aber auch keine beliebig austauschbaren Allgemeinplaetze ohne erkennbaren Bezug zur Schwachstelle.

FORMULIERUNG:
- Vermeide wiederkehrende Satzanfaenge.
- Nutze abwechslungsreiche, natuerliche Formulierungen.
- VERBOTEN: "Es wird empfohlen", "implementieren", "einrichten", "konfigurieren", "installieren"
- VERBOTEN: identische Satzkonstruktionen wie "Ein solches Konzept...", "Ein solches Verfahren..."
- VERBOTEN: juristische Formulierungen wie "ist angezeigt" - stattdessen "ist sinnvoll" oder "bietet sich an"
- Stattdessen z. B.: "Ziel ist der Aufbau von...", "Ein strukturierter Ansatz besteht darin...", "Hierfuer ist ein Konzept zu entwickeln, das..."

AUFWAND:
- gering = kleine organisatorische Anpassung, wenig Koordination
- mittel = bereichsuebergreifende Abstimmung, neue Prozesse definieren
- erhoeht = strukturelle Veraenderungen, mehrere Systeme/Bereiche betroffen
- Aufwand muss logisch zur Massnahme passen.

KONSISTENZ:
- Pro Schwachstelle genau eine Massnahme.
- Keine Zusammenfassung mehrerer Schwachstellen.
- Keine Dopplungen oder thematisch identischen Massnahmen.

ANTWORTFORMAT:
{"massnahmen":[{"schwachstelleId":"<id>","bezeichnung":"<max 10 Woerter>","beschreibung":"<2-3 Saetze, strategisch, Nutzen enthalten>","aufwand":"gering|mittel|erhoeht"}]}`;
  };

  const openSuggestPopup = async () => {
    let saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_BASE}/api/prompts?sectionKey=suggest-action-plan&cardId=bulk`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.promptText) {
            saved = data.promptText;
            localStorage.setItem(STORAGE_KEY, saved);
          }
        }
      } catch {}
    }
    // Immer frischen Prompt mit aktuellen Daten bauen
    setEditablePrompt(buildDefaultPrompt());
    setSuggestPromptSource('standard');
    setPromptError(null);
    setShowPromptPopup(true);
  };

  const callBulk = (prompt: string) =>
    suggestBulk<any>({
      sectionId: 'action-plan',
      customPrompt: prompt,
      context: {
        unternehmen: customerData?.name || '',
        branche: customerData?.wirtschaftszweig || '',
        berichtstitel: formData?.['berichtstitel'] || '',
      },
    });

  const parseMassnahmen = (rawItems: any[]): Massnahme[] => rawItems.map((item: any) => {
    if (typeof item === 'string') {
      return { id: generateId(), schwachstelleId: '', bezeichnung: item, beschreibung: '', aufwand: '' as const };
    }
    return {
      id: generateId(),
      schwachstelleId: item.schwachstelleId || '',
      bezeichnung: item.bezeichnung || item.text || '',
      beschreibung: item.beschreibung || '',
      aufwand: (['gering', 'mittel', 'erhoeht'].includes(item.aufwand) ? item.aufwand : '') as Massnahme['aufwand'],
    };
  });

  const validateMassnahmen = (entries: Massnahme[]): string[] => {
    const errors: string[] = [];
    const schwachstellenIds = new Set((schwachstellenData || []).map(s => s.id));
    const expectedCount = schwachstellenData?.length || 0;
    if (expectedCount > 0 && entries.length !== expectedCount) {
      errors.push(`Anzahl Massnahmen (${entries.length}) != Anzahl Schwachstellen (${expectedCount})`);
    }
    const usedIds = new Set<string>();
    entries.forEach((m, i) => {
      if (isEmptyContent(m.bezeichnung)) errors.push(`Massnahme ${i + 1}: bezeichnung leer`);
      if (isEmptyContent(m.beschreibung)) errors.push(`Massnahme ${i + 1}: beschreibung leer`);
      if (m.schwachstelleId && !schwachstellenIds.has(m.schwachstelleId)) {
        errors.push(`Massnahme ${i + 1}: schwachstelleId "${m.schwachstelleId}" existiert nicht`);
      }
      if (m.schwachstelleId && usedIds.has(m.schwachstelleId)) {
        errors.push(`Massnahme ${i + 1}: schwachstelleId "${m.schwachstelleId}" doppelt verwendet`);
      }
      if (m.schwachstelleId) usedIds.add(m.schwachstelleId);
      if (m.aufwand && !['gering', 'mittel', 'erhoeht'].includes(m.aufwand)) {
        errors.push(`Massnahme ${i + 1}: ungültiger Aufwand "${m.aufwand}"`);
      }
    });
    return errors;
  };

  // Returns { entries, error } – error ist gesetzt wenn keine Vorschlaege gefunden.
  const extractAndValidate = async (prompt: string, res: any): Promise<{ entries?: Massnahme[]; error?: string }> => {
    let items = res.massnahmen || res.empfehlungen || res.recommendations || [];
    if (!items?.length) {
      const firstArr = Object.values(res).find((v): v is any[] => Array.isArray(v) && v.length > 0);
      if (!firstArr) {
        return { error: `Keine Vorschläge gefunden. API-Antwort-Keys: ${Object.keys(res).join(', ')}` };
      }
      items = firstArr;
    }
    let newEntries = parseMassnahmen(items);
    const validationIssues = validateMassnahmen(newEntries);
    if (validationIssues.length > 0) {
      console.warn('[Section5] Strukturelle Fehler, Retry:', validationIssues);
      try {
        const retryPrompt = prompt + `\n\nWICHTIGER KORREKTURHINWEIS:\nDie vorherige Antwort hatte strukturelle Fehler:\n${validationIssues.map(e => `- ${e}`).join('\n')}\n\nKorrigiere diese Fehler. Stelle sicher:\n- Genau eine Massnahme pro Schwachstelle\n- Jede schwachstelleId muss exakt einer ID aus der Schwachstellenliste entsprechen\n- Jede schwachstelleId nur einmal verwenden\n- bezeichnung und beschreibung duerfen nicht leer sein\n- aufwand nur: gering, mittel, erhoeht`;
        const retryResult = await callBulk(retryPrompt);
        if (retryResult.ok) {
          const retryRes: any = retryResult.data;
          const retryItems = retryRes.massnahmen || retryRes.empfehlungen || retryRes.recommendations || [];
          if (retryItems.length > 0) {
            const retryEntries = parseMassnahmen(retryItems);
            const retryIssues = validateMassnahmen(retryEntries);
            if (retryIssues.length < validationIssues.length) {
              console.log('[Section5] Retry verbessert:', retryIssues.length, 'vs', validationIssues.length, 'Fehler');
              newEntries = retryEntries;
            }
          }
        }
      } catch (retryErr) {
        console.warn('[Section5] Retry fehlgeschlagen, verwende Original:', retryErr);
      }
    }
    return { entries: newEntries };
  };

  const applyEntries = (newEntries: Massnahme[]) => {
    setMassnahmenEntries(newEntries);
    onDataChange({ massnahmen: newEntries });
    setLastSyncedVersion(schwachstellenVersion || 0);
    setValidationErrors(new Set());
    try { localStorage.setItem('section5-inputs-backup', JSON.stringify({ massnahmen: newEntries })); } catch {}
  };

  const handleSuggestSubmit = async () => {
    setBulkSuggesting(true);
    setPromptError(null);
    localStorage.setItem(STORAGE_KEY, editablePrompt);
    try {
      const token = localStorage.getItem('auth_token');
      // DB-Backup (fire-and-forget)
      fetch(`${API_BASE}/api/prompts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sectionKey: 'suggest-action-plan', cardId: 'bulk', promptText: editablePrompt }),
      }).catch(() => {});

      const result = await callBulk(editablePrompt);
      if (!result.ok) throw new Error(formatGenerationError(result.error));
      const { entries, error } = await extractAndValidate(editablePrompt, result.data);
      if (error || !entries) {
        setPromptError(error || 'Unbekannter Fehler');
        return;
      }
      applyEntries(entries);
      setShowPromptPopup(false);
    } catch (err: any) {
      setPromptError(err.message || 'Vorschläge konnten nicht generiert werden');
    } finally {
      setBulkSuggesting(false);
    }
  };

  const handleDirectBulkSuggest = async () => {
    setTipsPopupOpen(true);
    setTipsPopupError(null);
    const prompt = buildDefaultPrompt();
    const result = await callBulk(prompt);
    if (!result.ok) {
      setTipsPopupError(formatGenerationError(result.error));
      const autoCloseMs = result.error.code === 'RATE_LIMITED' || result.error.code === 'ALREADY_RUNNING' ? 8000 : 6000;
      setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, autoCloseMs);
      return;
    }
    const { entries, error } = await extractAndValidate(prompt, result.data);
    if (error || !entries) {
      setTipsPopupError(error || 'Unbekannter Fehler');
      setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, 5000);
      return;
    }
    applyEntries(entries);
    setTipsPopupOpen(false);
  };

  // ── Styling ──────────────────────────────────────────────────
  const cardClass = (isOrphaned: boolean) => {
    const base = 'rounded-lg border p-4 relative';
    if (isOrphaned) return `${base} ${darkMode ? 'bg-orange-900/10 border-orange-700' : 'bg-orange-50 border-orange-300'}`;
    return `${base} ${darkMode ? 'bg-slate-750 border-slate-600' : 'bg-slate-50 border-slate-200'}`;
  };

  const inputClass = darkMode
    ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400'
    : 'bg-white border-slate-300';

  const errorBorder = 'border-red-400 ring-1 ring-red-400';

  const addBtnClass = darkMode
    ? 'border-slate-600 text-slate-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/20'
    : 'border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50';

  // ── Schwachstelle-Label Helper ───────────────────────────────
  const getSchwachstelleLabel = (id: string): string => {
    if (!schwachstellenData) return id;
    const idx = schwachstellenData.findIndex(s => s.id === id);
    if (idx === -1) return '(gelöscht)';
    const s = schwachstellenData[idx];
    return `${idx + 1}. ${s.problem || '(ohne Bezeichnung)'}`;
  };

  return (
    <>
    <div className={`rounded-lg shadow-sm flex-1 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
      <div className="mx-6 my-6">

        {/* Stale-Warnung */}
        {isStale && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-300'} border`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
              Schwachstellen wurden geändert. Bitte Maßnahmen prüfen und ggf. neu generieren.
            </p>
          </div>
        )}

        {/* Vorschläge generieren Button */}
        <button
          onClick={isAdmin ? openSuggestPopup : handleDirectBulkSuggest}
          disabled={!customerData}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 mb-5 rounded-lg font-medium text-sm transition-colors border-2 border-dashed ${
            !customerData
              ? 'opacity-50 cursor-not-allowed'
              : darkMode
                ? 'border-blue-500/50 text-blue-400 hover:bg-blue-900/20 hover:border-blue-400'
                : 'border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Vorschläge generieren
        </button>

        {/* Maßnahmen-Liste (einfache Textfelder) */}
        <div className="space-y-3">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            Empfohlene Maßnahmen
          </label>
          {massnahmenEntries.map((entry, index) => {
            const hasNoRef = !entry.schwachstelleId && !isEmptyContent(entry.bezeichnung);
            const isOrphaned = entry.schwachstelleId && !schwachstelleIds.has(entry.schwachstelleId);
            return (
            <div key={entry.id} className="space-y-1.5">
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-1.5">
                  <RichListInput
                    value={entry.bezeichnung}
                    onChange={(html) => updateEntry(entry.id, 'bezeichnung', html)}
                    placeholder="Beschreiben Sie eine empfohlene Maßnahme..."
                    darkMode={darkMode}
                  />
                  {/* Schwachstellen-Zuordnung Dropdown */}
                  <select
                    translate="no"                    value={entry.schwachstelleId}
                    onChange={(e) => updateEntry(entry.id, 'schwachstelleId', e.target.value)}
                    className={`w-full px-3 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasNoRef
                        ? darkMode ? 'border-amber-600 bg-slate-600 text-amber-300' : 'border-amber-400 bg-amber-50 text-amber-700'
                        : inputClass
                    }`}
                  >
                    <option value="">— Schwachstelle zuordnen —</option>
                    {(schwachstellenData || []).filter(s => !isEmptyContent(s.problem)).map((s, i) => {
                      const label = htmlToPlainText(s.problem).replace(/\n+/g, ' ');
                      return (
                        <option key={s.id} value={s.id}>
                          {i + 1}. {label.length > 60 ? label.substring(0, 60) + '…' : label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button
                  onClick={() => removeEntry(entry.id)}
                  disabled={massnahmenEntries.length === 1}
                  className={`p-2.5 rounded-lg border transition-colors ${
                    massnahmenEntries.length === 1
                      ? darkMode
                        ? 'border-slate-600 text-slate-500 cursor-not-allowed'
                        : 'border-slate-200 text-slate-300 cursor-not-allowed'
                      : darkMode
                        ? 'border-slate-600 text-slate-400 hover:bg-red-900/30 hover:border-red-500 hover:text-red-400'
                        : 'border-slate-300 text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Warnung: keine Zuordnung */}
              {hasNoRef && (
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs ${
                  darkMode ? 'text-amber-400 bg-amber-900/20' : 'text-amber-600 bg-amber-50'
                }`}>
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Keine Schwachstelle zugeordnet – bitte oben auswählen</span>
                </div>
              )}
              {/* Warnung: verwaiste Zuordnung */}
              {isOrphaned && (
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs ${
                  darkMode ? 'text-red-400 bg-red-900/20' : 'text-red-600 bg-red-50'
                }`}>
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Zugeordnete Schwachstelle wurde gelöscht – bitte neu zuordnen</span>
                </div>
              )}
            </div>
            );
          })}
          <button
            onClick={addEntry}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed transition-colors text-sm font-medium ${addBtnClass}`}
          >
            <Plus className="w-4 h-4" />
            Weitere Maßnahme hinzufügen
          </button>
        </div>

        {/* Text generieren */}
        <div className={`flex justify-end pt-6 mt-6 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
          <button
            onClick={handleGenerate}
            className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm font-medium shadow-sm ${
              darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            ✨ Text generieren
          </button>
        </div>
      </div>
    </div>

    {/* Prompt Popup */}
    {showPromptPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!bulkSuggesting ? () => setShowPromptPopup(false) : undefined} />
        <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl shadow-2xl ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Vorschläge generieren</h2>
                <div className={`flex items-center gap-2 text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  <span className={`px-1.5 py-0.5 rounded font-medium ${
                    suggestPromptSource === 'standard'
                      ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                      : suggestPromptSource === 'lokal'
                        ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        : darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {suggestPromptSource === 'standard' ? 'Standard' : suggestPromptSource === 'lokal' ? 'Lokal gespeichert' : 'Bearbeitet'}
                  </span>
                  <span>· {editablePrompt.length} Z. · {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
            <button onClick={() => setShowPromptPopup(false)} disabled={bulkSuggesting} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'} disabled:opacity-50`}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prompt (bearbeitbar)</label>
              <textarea translate="no" value={editablePrompt} onChange={(e) => { setEditablePrompt(e.target.value); setSuggestPromptSource('bearbeitet'); }} disabled={bulkSuggesting} rows={16} className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y font-mono leading-relaxed ${darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-slate-50 border-slate-300 text-slate-900'} disabled:opacity-60`} />
            </div>
            {promptError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-300">{promptError}</p>
              </div>
            )}
            {bulkSuggesting && (
              <div className={`p-3 rounded-lg flex items-center gap-3 ${darkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'} border`}>
                <Loader2 className={`w-4 h-4 animate-spin ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Vorschläge werden generiert...</p>
              </div>
            )}
          </div>
          <div className={`flex items-center justify-between p-4 sm:p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setEditablePrompt(buildDefaultPrompt());
                setSuggestPromptSource('standard');
                setPromptError(null);
              }}
              disabled={bulkSuggesting}
              className={`text-xs px-3 py-1.5 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'} disabled:opacity-50`}
            >
              ↺ Prompt zurücksetzen
            </button>
            <button onClick={handleSuggestSubmit} disabled={bulkSuggesting || !editablePrompt.trim()} className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <span className="inline-flex">
                {bulkSuggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </span>
              <span>{bulkSuggesting ? 'Wird generiert...' : 'Abschicken'}</span>
            </button>
          </div>
        </div>
      </div>
    )}

    <GeneratingTipsPopup isOpen={tipsPopupOpen} darkMode={darkMode} error={tipsPopupError} />
    </>
  );
}
